import Modal from 'antd/lib/modal/Modal';
import * as React from 'react';
import { LoadingOutlined, CloseOutlined } from '@ant-design/icons';
import { antGrey, colorWithAlpha } from './styles/globalStyles';
import Space from 'antd/lib/space';
import Spin from 'antd/lib/spin';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button/button';
import { useDispatch } from 'react-redux';
import { ReleaseUpdatable } from '../actions/updatable';
import { SubmitAppForReviewRequestReleaseStep, tryUpdateRelease } from '../actions/release';
import { displayCompanyForPlatform } from '../helpers/displayHelpers';
import { AppPlatform, AppStoreType, RunwayResponseType } from '../types';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../typescriptHelpers';
import { globalMessage } from '../utils/globalMessage';
import { captureMessage } from '@sentry/react';
import { StepRunUpdatable, SubmitAppForReviewRequest } from '@/actions/stepRun';
import { useCallUpdatable, useUpdated, useError } from '@/utils/hooks/helpers';

interface SubmissionModalProps extends ReleaseComponentProps {
    open: boolean;
    displayableVersion: string;
    releasePreviousSubmissionToAllUsersFirst: boolean;
    releaseBetaTrackToFullPercentagePriorToSubmission: boolean;
    shouldMatchPreviousPhasedRelease: boolean;
    selectedBuildNumbers: string[];
    platform: AppPlatform;
    appStoreType: AppStoreType;
    onSuccess: () => void;
    onCancel: () => void;
    reviewSubmissionWithoutAppVersion: boolean;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

const SubmissionModal: React.FC<SubmissionModalProps> = (props: SubmissionModalProps) => {
    const { releaseId, appId, open } = props;
    const dispatch = useDispatch();

    const [cancelButtonDisabled, setCancelButtonDisabled] = React.useState(false);

    const [submitAppForReviewStepRun, _, submitAppForReviewResponseTypeStepRun, submitAppForReviewErrorStepRun] =
        useCallUpdatable(StepRunUpdatable.SubmitAppForReview, props.stepRunComponentProps);

    const submitAppForReviewSuccessReleaseStep = useUpdated(ReleaseUpdatable.SubmitAppForReview, releaseId);
    const submitAppForReviewErrorReleaseStep = useError(ReleaseUpdatable.SubmitAppForReview, releaseId);

    const submitSuccess =
        props.stepRunComponentProps ?
            submitAppForReviewResponseTypeStepRun === RunwayResponseType.Success
        :   submitAppForReviewSuccessReleaseStep;

    const submitError =
        props.stepRunComponentProps ? submitAppForReviewErrorStepRun : submitAppForReviewErrorReleaseStep;

    const reviewSubmissionWithoutAppVersion = props.reviewSubmissionWithoutAppVersion;

    const submitForReview = () => {
        if (props.stepRunComponentProps) {
            const request: SubmitAppForReviewRequest = {
                releasePreviousSubmissionToAllUsersFirst: props.releasePreviousSubmissionToAllUsersFirst,
                releaseBetaTrackToFullPercentagePriorToSubmission:
                    props.releaseBetaTrackToFullPercentagePriorToSubmission,
                shouldMatchPreviousPhasedRelease: props.shouldMatchPreviousPhasedRelease,
            };
            submitAppForReviewStepRun(request);
        } else {
            const request: SubmitAppForReviewRequestReleaseStep = {
                orgId: props.orgId,
                appId: appId,
                releaseId: releaseId,
                releasePreviousSubmissionToAllUsersFirst: props.releasePreviousSubmissionToAllUsersFirst,
                releaseBetaTrackToFullPercentagePriorToSubmission:
                    props.releaseBetaTrackToFullPercentagePriorToSubmission,
                shouldMatchPreviousPhasedRelease: props.shouldMatchPreviousPhasedRelease,
            };
            dispatch(tryUpdateRelease(ReleaseUpdatable.SubmitAppForReview, request, releaseId));
        }
    };

    React.useEffect(() => {
        if (open) {
            const timeoutID = setTimeout(() => {
                setCancelButtonDisabled(true);
                submitForReview();
            }, 3000);

            return () => {
                clearTimeout(timeoutID);
            };
        }

        return () => {
            setCancelButtonDisabled(false);
        };
    }, [open]);

    React.useEffect(() => {
        if (!reviewSubmissionWithoutAppVersion && submitSuccess) {
            let buildsText = ' ';
            if (props.selectedBuildNumbers.length > 0) {
                buildsText =
                    props.selectedBuildNumbers.length > 1 ?
                        ` (Builds ${props.selectedBuildNumbers.join(', ')}) `
                    :   ` (Build ${props.selectedBuildNumbers[0]}) `;
            } else {
                captureMessage(`No selected build numbers on submission modal for app ${appId}, release ${releaseId}`);
            }

            const successMessage =
                props.releasePreviousSubmissionToAllUsersFirst ?
                    `Your previous release was rolled out to 100% and version ${
                        props.displayableVersion
                    }${buildsText}was submitted to ${displayCompanyForPlatform(
                        props.platform,
                        props.appStoreType,
                    )} for review!`
                :   `Version ${props.displayableVersion}${buildsText}was submitted to ${displayCompanyForPlatform(
                        props.platform,
                        props.appStoreType,
                    )} for review!`;

            globalMessage.success(successMessage);

            props.onSuccess();

            setCancelButtonDisabled(false);
        }
    }, [submitSuccess]);

    React.useEffect(() => {
        if (reviewSubmissionWithoutAppVersion) {
            globalMessage.info(
                'An existing in progress review submission was detected in App Store Connect. Your app version has been added to the existing submission – please review the updated submission and included items. You can re-submit from Runway when ready.',
            );
            props.onSuccess();

            setCancelButtonDisabled(false);
        }
    }, [reviewSubmissionWithoutAppVersion]);

    React.useEffect(() => {
        if (submitError) {
            globalMessage.error(submitError);

            props.onCancel();

            setCancelButtonDisabled(false);
        }
    }, [submitError]);

    const backgroundColor = colorWithAlpha(antGrey.grey10, 0.8);

    return (
        <Modal
            open={open}
            maskStyle={{ backgroundColor: backgroundColor }}
            footer={null}
            centered
            closable={false}
            className="modal-submission"
        >
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Space direction="vertical" size="large" align="center">
                    <Spin
                        indicator={<LoadingOutlined style={{ color: 'white', fontSize: 50 }} size={100} />}
                        size="large"
                        spinning={props.open}
                    />
                    <Text style={{ color: 'white', fontSize: 24, fontWeight: 600 }}>Submitting for review...</Text>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                            ghost
                            icon={
                                <CloseOutlined
                                    style={cancelButtonDisabled ? { color: antGrey.grey6 } : { color: 'white' }}
                                />
                            }
                            onClick={() => props.onCancel()}
                            disabled={cancelButtonDisabled}
                            style={
                                cancelButtonDisabled ?
                                    {
                                        color: antGrey.grey6,
                                        backgroundColor: 'transparent',
                                        borderColor: antGrey.grey5,
                                    }
                                :   { color: 'white', backgroundColor: 'transparent' }
                            }
                        >
                            Cancel submission
                        </Button>
                    </div>
                </Space>
            </div>
        </Modal>
    );
};

export default SubmissionModal;
